import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRouter } from 'next/router';
import { AlertType, useAlertContext } from './alertContext';
import { Metrics } from './metrics';
import { useModalContext } from './modalContext';
import { MOMENT_TEMPLATES_QUERY, MomentTemplateFields } from './queries';
import {
  DeleteMomentTemplateMutation,
  DeleteMomentTemplateMutationVariables,
  MomentTemplateFields_SalvationMoment_Fragment,
  MomentType,
  SaveMomentTemplateMutation,
  SaveMomentTemplateMutationVariables,
} from '@generated/types';

interface Props {
  callSaveMomentTemplate: (variables: SaveMomentTemplateMutationVariables) => void;
  callDeleteMomentTemplate: (id: string) => void;
}

export const SAVE_MOMENT_TEMPLATE = gql`
  mutation SaveMomentTemplate($input: MomentTemplateInput!) {
    saveMomentTemplate(input: $input) {
      momentTemplate {
        ...MomentTemplateFields
      }
      errors {
        code
        message
      }
    }
  }
  ${MomentTemplateFields}
`;

export const DELETE_MOMENT_TEMPLATE = gql`
  mutation DeleteMomentTemplate($id: ID!) {
    deleteMomentTemplate(id: $id) {
      momentTemplate {
        ...MomentTemplateFields
      }
      errors {
        code
        message
      }
    }
  }
  ${MomentTemplateFields}
`;

const useMomentTemplateQueries = (): Props => {
  const router = useRouter();
  const { openAlert } = useAlertContext();
  const { closeModal } = useModalContext();

  const [saveMomentTemplate] = useMutation<
    SaveMomentTemplateMutation,
    SaveMomentTemplateMutationVariables
  >(SAVE_MOMENT_TEMPLATE, {
    onCompleted: data => {
      if (data.saveMomentTemplate?.errors.length === 0 && data.saveMomentTemplate.momentTemplate) {
        // This cast is not ideal, but because of the Union type there isn't really a better way without a huge switch statement.
        // Each union option will have an id
        const { id } = data.saveMomentTemplate
          .momentTemplate as MomentTemplateFields_SalvationMoment_Fragment;
        Metrics.momentTemplateSaved(id);
        openAlert(AlertType.SUCCESS, 'Moment saved.');
        if (data.saveMomentTemplate.momentTemplate.type === MomentType.NEXT_STEPS) {
          router.push('/next_steps/');
        } else {
          router.push('/moments');
        }
      } else {
        openAlert(AlertType.ERROR, 'An error occurred.');
      }
    },
    refetchQueries: [{ query: MOMENT_TEMPLATES_QUERY }],
  });

  const [deleteMomentTemplate] = useMutation<
    DeleteMomentTemplateMutation,
    DeleteMomentTemplateMutationVariables
  >(DELETE_MOMENT_TEMPLATE, {
    onCompleted: data => {
      if (data.deleteMomentTemplate?.errors.length === 0) {
        const { id } = data.deleteMomentTemplate
          ?.momentTemplate as MomentTemplateFields_SalvationMoment_Fragment;

        if (id) {
          Metrics.momentTemplateDeleted(id);
        }

        closeModal();

        if (data.deleteMomentTemplate.momentTemplate?.type === MomentType.NEXT_STEPS) {
          openAlert(AlertType.SUCCESS, 'Next Step deleted.');
          router.push('/next_steps');
        } else {
          openAlert(AlertType.SUCCESS, 'Moment deleted.');
          router.push('/moments');
        }
      } else {
        openAlert(AlertType.ERROR, 'An error occurred.');
      }
    },
    update: (cache, data) => {
      // This cast is not ideal, but because of the Union type there isn't really a better way without a huge switch statement.
      // Each union option will have an id
      const { id } = data?.data?.deleteMomentTemplate
        ?.momentTemplate as MomentTemplateFields_SalvationMoment_Fragment;
      cache.evict({ id: `${data.data?.deleteMomentTemplate?.momentTemplate?.__typename}:${id}` });
      cache.gc();
    },
  });

  const callSaveMomentTemplate = (variables: SaveMomentTemplateMutationVariables): void => {
    saveMomentTemplate({
      variables,
    });
  };

  const callDeleteMomentTemplate = (id: string): void => {
    deleteMomentTemplate({
      variables: {
        id,
      },
    });
  };

  return { callSaveMomentTemplate, callDeleteMomentTemplate };
};

export { useMomentTemplateQueries };
